import { useState } from 'react';
import { 
  FaMapMarkerAlt, 
  FaPaperPlane,
  FaChalkboardTeacher,
  FaLaptopCode,
  FaGlobeAmericas,
  FaUserGraduate,
  FaUsers,
  FaLightbulb,
  FaHeart
} from 'react-icons/fa';
const WEB3FORM_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: ''
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!WEB3FORM_ACCESS_KEY) {
      console.error('WEB3FORM_ACCESS_KEY is not configured');
      alert('Form temporarily unavailable. Please try again later.');
      return;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORM_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          position: formData.position,
          experience: formData.experience,
          message: formData.message,
          subject: `Career Application - ${formData.position}`,
          from_name: 'The Lingua Alliance - Career Application'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('Career application submitted successfully:', result);
        alert('Thank you for your application! We will review it and get back to you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          message: ''
        });
        closeFormModal();
      } else {
        console.error('Form submission failed:', result);
        alert('Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };



  const openJobModal = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
  };

  const closeJobModal = () => {
    setShowJobModal(false);
  };

  const openFormModal = (jobTitle = '') => {
    setFormData({ ...formData, position: jobTitle });
    setShowJobModal(false);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
  };

  const whyWorkWithUs = [
    {
      icon: <FaGlobeAmericas />,
      title: 'Make Global Impact',
      description: 'Help students achieve their dreams of studying abroad, migrating, and building international careers'
    },
    {
      icon: <FaUserGraduate />,
      title: 'Grow Your Expertise',
      description: 'Continuous learning opportunities, workshops, and certification support to enhance your skills'
    },
    {
      icon: <FaUsers />,
      title: 'Collaborate With Experts',
      description: 'Work alongside certified trainers and industry professionals in a knowledge-rich environment'
    },
    {
      icon: <FaLightbulb />,
      title: 'Innovate & Inspire',
      description: 'Freedom to bring creative teaching methods and contribute to curriculum development'
    },
    {
      icon: <FaHeart />,
      title: 'Supportive, Inclusive Culture',
      description: 'Be part of a team that values diversity, collaboration, and work-life balance'
    }
  ];

  const positions = [
    {
      title: 'French Language Trainer',
      type: 'Full-Time / Part-Time',
      location: 'Remote',
      levels: 'A1 - B2',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'We\'re looking for a passionate French educator who can deliver interactive, CEFR-aligned sessions that help students speak confidently and crack DELF with ease.',
      responsibilities: [
        'Teach A1–B2 levels (online/offline)',
        'Prepare students for DELF examinations',
        'Conduct speaking practice & mock tests',
        'Create engaging lesson plans and activities',
        'Track student progress and provide feedback'
      ],
      requirements: [
        'DELF B2/C1 certification preferred',
        'Strong communication & teaching skills',
        'Experience with online tools (Zoom/Meet)',
        'Freshers with strong proficiency may also apply'
      ]
    },
    {
      title: 'German Language Trainer',
      type: 'Full-Time / Part-Time',
      location: 'Remote',
      levels: 'A1 - B2',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'Help learners master German for studies, work, and migration. Deliver structured Goethe-aligned lessons that build confidence fast.',
      responsibilities: [
        'Teach A1–B2 levels',
        'Prepare students for Goethe exams',
        'Conduct regular communication sessions',
        'Manage learning plans & student assessments',
        'Provide doubt-solving support'
      ],
      requirements: [
        'Goethe B2/C1 certification preferred',
        'Prior teaching experience is a plus',
        'Excellent grammar and speaking clarity'
      ]
    },
    {
      title: 'Spanish Language Trainer',
      type: 'Full-Time / Part-Time',
      location: 'Pune / Remote',
      levels: 'A1 - B2',
      department: 'Language Instruction',
      icon: <FaChalkboardTeacher />,
      description: 'Lead immersive Spanish sessions that build communication skills and prepare students for DELE exams.',
      responsibilities: [
        'Conduct online/offline Spanish classes',
        'Prepare study material & assignments',
        'Carry out speaking drills and listening practice',
        'Prepare students for DELE examinations',
        'Evaluate student progress'
      ],
      requirements: [
        'DELE B2/C1 certification preferred',
        'Teaching experience beneficial',
        'Strong cultural & linguistic knowledge'
      ]
    },
    {
      title: 'Education Content Creator',
      type: 'Full-Time',
      location: 'Hybrid',
      levels: 'All Levels',
      department: 'Content & Curriculum',
      icon: <FaLaptopCode />,
      description: 'We\'re seeking a creative content specialist to develop academic resources for French/German/Spanish/Japanese learners. You\'ll create lesson content, worksheets, reels, and micro-learning modules.',
      responsibilities: [
        'Create educational content for language courses',
        'Develop grammar notes, worksheets, quizzes',
        'Script and ideate video content for social media',
        'Work with trainers to refine curriculum',
        'Support branding with high-quality educational visuals'
      ],
      requirements: [
        'Strong command over one foreign language (A2+ minimum)',
        'Excellent writing & content development skills',
        'Creativity + understanding of learner psychology',
        'Experience with Canva / Notion / basic editing tools is a plus'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink overflow-x-hidden">
      {/* Job Details Modal */}
      {showJobModal && selectedJob && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full my-8">
              {/* Close Button */}
              <button
                onClick={closeJobModal}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Content */}
              <div className="p-6 sm:p-8">
              {/* Job Header */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-2xl text-primary shadow-lg flex-shrink-0">
                  {selectedJob.icon}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-ink mb-3">{selectedJob.title}</h2>
                  <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-primary/10 text-xs sm:text-sm font-medium text-primary">{selectedJob.type}</span>
                    <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-primary/10 text-xs sm:text-sm font-medium text-primary">{selectedJob.department}</span>
                    <span className="flex items-center gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-primary/10 text-xs sm:text-sm font-medium text-primary">
                      <FaMapMarkerAlt />
                      {selectedJob.location}
                    </span>
                    {selectedJob.levels && (
                      <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-primary/10 text-xs sm:text-sm font-medium text-primary">
                        {selectedJob.levels}
                      </span>
                    )}
                  </div>
                  <p className="text-sm sm:text-base lg:text-lg text-ink-muted leading-relaxed">{selectedJob.description}</p>
                </div>
              </div>

              {/* Responsibilities */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-semibold text-ink mb-3 sm:mb-4 flex items-center gap-3">
                  <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-primary to-primary-soft rounded-full"></div>
                  Key Responsibilities
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {selectedJob.responsibilities.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-ink-muted">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-semibold text-ink mb-3 sm:mb-4 flex items-center gap-3">
                  <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-primary to-primary-soft rounded-full"></div>
                  Requirements & Qualifications
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {selectedJob.requirements.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-ink-muted">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Apply Button */}
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => openFormModal(selectedJob.title)}
                  className="rounded-full bg-gradient-to-r from-primary to-primary-soft px-6 sm:px-8 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-wide text-white shadow-[0_12px_24px_rgba(79,64,198,0.3)] sm:shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_18px_34px_rgba(18,10,56,0.3)] sm:hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] inline-flex items-center gap-2">
                  <FaPaperPlane />
                  Apply for this Position
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Application Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-3xl shadow-2xl max-w-3xl w-full my-8">
              {/* Close Button */}
              <button
                onClick={closeFormModal}
                className="absolute top-6 right-6 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Form Content */}
              <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-semibold mb-4">Submit Your Application</h2>
                <p className="text-ink-muted">
                  Fill out the form below to apply for this position
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder="+91 90000 00000"
                    />
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-ink mb-2">
                      Position of Interest <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="position"
                      name="position"
                      required
                      value={formData.position}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="">Select a position</option>
                      {positions.map((pos, idx) => (
                        <option key={idx} value={pos.title}>{pos.title}</option>
                      ))}
                      <option value="other">Other / General Application</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-ink mb-2">
                    Years of Experience
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select experience level</option>
                    <option value="0-1">0-1 years</option>
                    <option value="2-5">2-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-ink mb-2">
                    Cover Letter / Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Tell us why you're interested in joining our team..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] flex items-center justify-center gap-2"
                >
                  <FaPaperPlane />
                  Submit Application
                </button>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 bg-gradient-to-br from-primary/10 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,64,198,0.1),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(79,64,198,0.08),transparent_50%)]" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary-soft/20 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold bg-gradient-to-r from-primary to-primary-soft bg-clip-text text-transparent pb-2">
              Careers at The Lingua Alliance
            </h1>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-ink">
            Join a Team That's Shaping Global Communicators.
          </h2>
          <p className="max-w-3xl text-sm sm:text-base lg:text-lg leading-relaxed text-ink-muted">
            At The Lingua Alliance, every class you teach, every piece of content you create, and every student you guide becomes a part of someone's life-changing journey.
          </p>
          <p className="max-w-3xl text-sm sm:text-base lg:text-lg leading-relaxed text-ink-muted">
            We are not just an institute - we are a movement that empowers thousands to study abroad, migrate, build global careers, and achieve dreams they once thought impossible.
          </p>
          <p className="max-w-2xl text-sm sm:text-base lg:text-lg font-medium text-primary">
            If you're passionate about languages, learning, and impact - your next big opportunity starts here.
          </p>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white border-b border-primary/10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary">
              Why Join Us
            </span>
            <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold">Why Work With Us?</h2>
            <p className="mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base text-ink-muted">
              Be part of something bigger. Join a team that values growth, impact, and collaboration.
            </p>
          </div>

          <div className="space-y-6 sm:space-y-8">
            {/* First Row - 3 Cards */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8">
              {whyWorkWithUs.slice(0, 3).map((benefit, index) => (
                <div
                  key={index}
                  className="w-full sm:w-[calc(50%-12px)] md:w-80 rounded-xl sm:rounded-2xl border border-primary/10 bg-gradient-to-br from-primary/5 to-white p-4 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
                >
                  <div className="inline-flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl sm:text-2xl text-primary mb-3 sm:mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-ink mb-2 sm:mb-3">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Second Row - 2 Cards Centered */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8">
              {whyWorkWithUs.slice(3, 5).map((benefit, index) => (
                <div
                  key={index + 3}
                  className="w-full sm:w-[calc(50%-12px)] md:w-80 rounded-xl sm:rounded-2xl border border-primary/10 bg-gradient-to-br from-primary/5 to-white p-4 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
                >
                  <div className="inline-flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl sm:text-2xl text-primary mb-3 sm:mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-ink mb-2 sm:mb-3">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary">
              Open Positions
            </span>
            <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold">Current Openings</h2>
            <p className="mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base text-ink-muted">
              Full-time / Part-time / Remote options available depending on role
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
            {positions.map((position, index) => (
              <div
                key={index}
                onClick={() => openJobModal(position)}
                className="group rounded-xl sm:rounded-2xl border border-primary/10 bg-white p-4 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)] cursor-pointer"
              >
                <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-lg sm:text-xl text-primary flex-shrink-0 group-hover:scale-110 transition-transform">
                    {position.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-ink group-hover:text-primary transition-colors mb-2">{position.title}</h3>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 rounded-full bg-primary/10 text-[10px] sm:text-xs font-medium text-primary">{position.type}</span>
                      <span className="px-2 py-1 rounded-full bg-primary/10 text-[10px] sm:text-xs font-medium text-primary">{position.department}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-ink-muted">
                    <FaMapMarkerAlt className="text-primary flex-shrink-0" />
                    <span><strong>Location:</strong> {position.location}</span>
                  </div>
                  {position.levels && (
                    <div className="flex items-center gap-2 text-sm text-ink-muted">
                      <FaChalkboardTeacher className="text-primary flex-shrink-0" />
                      <span><strong>Levels:</strong> {position.levels}</span>
                    </div>
                  )}
                </div>

                <p className="text-sm text-ink-muted leading-relaxed mb-4">{position.description}</p>
                
                <div className="flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
                  View Full Details & Apply
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upload Resume Form */}
      <section className="py-20 bg-canvas" id="application-form">

        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold mb-4">Submit Your Application</h2>
              <p className="text-ink-muted">
                Don't see a position that matches? Upload your resume and we'll keep you in mind for future opportunities
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="+91 90000 00000"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-ink mb-2">
                    Position of Interest
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a position</option>
                    {positions.map((pos, idx) => (
                      <option key={idx} value={pos.title}>{pos.title}</option>
                    ))}
                    <option value="other">Other / General Application</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-ink mb-2">
                  Years of Experience
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select experience level</option>
                  <option value="0-1">0-1 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-ink mb-2">
                  Cover Letter / Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-primary/20 bg-white px-4 py-3 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Tell us why you're interested in joining our team, your relevant experience, and what you can bring to The Lingua Alliance..."
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] flex items-center justify-center gap-2"
              >
                <FaPaperPlane />
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Career;

